import {SweetError} from 'app/utils/errors';
import {gt} from 'app/utils/i18n';
import {get} from 'app/utils/request';

type EmbedlyParamsType = Partial<{
  secure: true;
  maxwidth: number;
  frame: boolean;
  autoplay: boolean;
}>;

const EMBEDLY_OEMBED_API_URL = 'https://api.embed.ly/1/oembed';

export const DEFAULT_THUMBNAIL = 'images/default-video.png';

const DEFAULT_OPTS = {
  secure: true,
  maxwidth: 1000,
  frame: true,
} as const;

interface oEmbedLinkType {
  provider_name: string;
  provider_url: string;
  type: 'link';
  url: string;
  version: string;
}

interface oEmbedCommonType {
  version: string;
  title: string;
  author_name?: string;
  author_url?: string;
  provider_name?: string;
  provider_url?: string;
  thumbnail_url?: string;
  thumbnail_width?: number;
  thumbnail_height?: number;
}

interface oEmbedPhotoType extends oEmbedCommonType {
  type: 'photo';
  url: string;
  width: number;
  height: number;
}
interface oEmbedVideoType extends oEmbedCommonType {
  type: 'video';
  html: string;
  width: number;
  height: number;
}

interface oEmbedRichType extends oEmbedCommonType {
  type: 'rich';
  html: string;
  width: number;
  height: number;
}

export type oEmbedType =
  | oEmbedPhotoType
  | oEmbedVideoType
  | oEmbedLinkType
  | oEmbedRichType;

export const generateEmbedlyOembed = (
  url: string,
  key: string,
  opts: EmbedlyParamsType
): Promise<oEmbedType> =>
  get(EMBEDLY_OEMBED_API_URL, {...DEFAULT_OPTS, ...opts, url, key}).then(
    handleUnsupportedProvider
  );

const handleUnsupportedProvider = (oembed: oEmbedType): oEmbedType => {
  // use iframes for some providers. We figure they are OK.
  if (
    oembed.provider_url === 'https://screencast-o-matic.com' &&
    oembed.type === 'link'
  ) {
    // link looks like https://screencast-o-matic.com/watch/cqf2rbZYj4
    const parts = oembed.url.split('/');
    const code = parts[parts.length - 1].match(/[a-zA-Z0-9]+/);

    if (code == null) {
      // invalid code don't trust.
      return oembed;
    }

    return {
      type: 'video',
      html: `<iframe frameborder="0" scrolling="no" src="https://screencast-o-matic.com/embed?sc=${code[0]}&v=5&ff=1" allowfullscreen="true"></iframe>`,
      // placeholder isn't used.
      width: 1280,
      // placeholder isn't used.
      height: 868,
      title: '',
      version: oembed.version,
      provider_url: oembed.provider_url,
      provider_name: oembed.provider_name,
    };
  } else {
    return oembed;
  }
};

export const generateImageDataFromEmbedly = (
  url: string,
  key: string,
  opts: Record<string, number | string | boolean>,
  staticURL: string
) =>
  generateEmbedlyOembed(url, key, opts).then(
    (oembed) => {
      if ('html' in oembed && oembed.html != null && oembed.type === 'video') {
        const defaultThumb = `${staticURL}${DEFAULT_THUMBNAIL}`;
        return {
          embed_html: oembed.html,
          embed_ref: url,
          embed_thumb:
            oembed.thumbnail_url != null ? oembed.thumbnail_url : defaultThumb,
        };
      } else {
        let message, provider;

        if (oembed.type !== 'video') {
          provider =
            oembed.provider_name != null
              ? oembed.provider_name
              : gt('that provider');
          message = gt(
            "We couldn't recognize that video URL for %(provider)s",
            {
              provider,
            }
          );
        } else {
          provider =
            oembed.provider_name != null
              ? oembed.provider_name
              : gt("This provider's");
          message = gt('%(provider)s videos are not supported', {
            provider,
          });
        }

        const error = new Error(message);
        // @ts-expect-error TS doesn't accept custom Error property
        error.oembed = oembed;
        throw error;
      }
    },
    (embedlyError: SweetError | Error) => {
      let message;

      if ('response' in embedlyError && embedlyError.response?.status === 404) {
        message = gt('That video url is no longer available (404 error)');
      } else {
        message = gt("Couldn't load video data at the moment");
      }

      const error = new Error(message);
      // @ts-expect-error TS doesn't accept custom Error property
      error.embedlyError = embedlyError;
      throw error;
    }
  );
